import React, { Fragment } from "react";
import moment from "moment";
import { Table } from "react-bootstrap";
import { Space, Switch, Tag } from 'antd';
import ButtonIcon from "./ButtonComp";
import NoData from "./NoData";
import { formatToSeconds, getCurrency, getKmFormat, getPercentaje } from "../libs/functions";
import SkeletonTable from "./SkeletonTable";

// Definición de tipos e interfaces
import { ActionButton, ActionSwitch, TableCompProps } from "../interfaces/ITableComp";


const TableComp: React.FC<TableCompProps> = ({
    headers,
    body,
    keys,
    actions,
    loading,
    type,
    loadingItems,
    page,
    badges,
    badges_values,
    currency,
    icons,
    percentage,
    km,
    moment_dates,
    seconds
}) => {

    const renderSwitch = (item: any) => {
        switch (type) {
            case 'switch':
                const {
                    checkedChildren,
                    unCheckedChildren,
                    onChange,
                    getChecked,
                    getDisabled
                } = actions as ActionSwitch;

                return (
                    <Switch
                        checkedChildren={checkedChildren || 'True'}
                        unCheckedChildren={unCheckedChildren || 'False'}
                        onChange={(checked) => onChange(checked, item.id)}
                        defaultChecked={getChecked(item.id)}
                        disabled={getDisabled(item.id)}
                    />
                );
            case 'btn_din':
                break;
            default:
                let currentActions: ActionButton[] = [];

                if (typeof actions === 'function') {
                    currentActions = actions(item);
                } else if (Array.isArray(actions)) {
                    currentActions = actions;
                }

                const new_actions = currentActions.filter((action) => {
                    if (action.dinamic) {
                        for (const element of action.dinamic) {
                            const { key, value } = element;
                            if (item[key] !== value)
                                return false;
                        }
                    }
                    if (action.dinamics) {
                        for (const element of action.dinamics) {
                            const { key, values } = element;
                            if (!values.includes(item[key]))
                                return false;
                        }
                    }
                    if (action.showWhenNotNull) {
                        for (const key of action.showWhenNotNull) {
                            if (item[key] === null) {
                                return false;
                            }
                        }
                    }
                    return true;
                });
                return new_actions.map((action, a_index) => (
                    <ButtonIcon
                        key={a_index}
                        name={action?.name || ""}
                        onClick={() => action.handleClick(item)}
                        icon={action.icon}
                        tooltip={action?.tooltip}
                        variant={action.variant}
                        disabled={action?.disabled || false}
                        spin={action.spin}
                    />
                ));
        }
    };

    const setBadge = (index_col: number, item: any, index_row: number) => {
        let cell_changes = true;

        // Buscar posiciones en los arreglos de configuración
        const pos = badges?.findIndex((element) => element.index === index_col) ?? -1;
        const pos_c = currency?.includes(index_col) ? index_col : -1;
        const pos_p = percentage?.findIndex((element) => element.index === index_col) ?? -1;
        const pos_km = km?.findIndex((element) => element.index === index_col) ?? -1;
        const pos_dates = moment_dates?.findIndex((element) => element.index === index_col) ?? -1;
        const pos_seconds = seconds?.findIndex((element)=>element.index === index_col) ?? -1;

        let cell: string | number | JSX.Element = '';

        if (keys[index_col] === '#') {
            cell = index_row + 1;
            if (page && loadingItems) {
                cell = (page - 1) * loadingItems + cell;
            }
        } else {
            cell = getCellValue(keys[index_col], item);

            // Buscar si hay un ícono asociado al valor de la celda
            const pos_i = icons?.findIndex((icon) => icon.value === cell) ?? -1;
            if (pos_i !== -1 && icons) {
                cell = icons[pos_i].icon;
                cell_changes = false;
            }
        }

        // Aplicar transformaciones según el tipo de dato
        if (cell_changes) {
            if (pos_dates !== -1) {
                cell = getMomentDate(cell, moment_dates![pos_dates].format);
            }
            if (pos_c !== -1) {
                const numericValue = Number(cell);
                if (!isNaN(numericValue)) {
                    cell = getCurrency(numericValue);
                }
            }
            if (pos_p !== -1) {
                const numericValue = Number(cell);
                if (!isNaN(numericValue)) {
                    cell = getPercentaje(numericValue, percentage![pos_p].accuracy);
                }
            }
            if (pos_km !== -1) {
                cell = getKmFormat(cell);
            }
            if (pos_seconds !== -1 && seconds) {
                cell = formatToSeconds(cell, seconds[pos_seconds].format)
            }
            if (pos !== -1) {
                let color = 'purple';

                if (badges_values) {
                    color = getColor(item, keys[index_col]);
                    cell = getCellName(item, keys[index_col]);
                } else if (badges![pos]?.color) {
                    const colorKey = badges![pos].color;
                    // Evita que `undefined` cause problemas
                    color = colorKey ? item[colorKey] : 'purple';
                }


                cell = <Tag color={color}>{cell}</Tag>;
            }
        }

        return (
            <td key={index_col}>
                <center>{cell}</center>
            </td>
        );
    };

    const getMomentDate = (cell: any, format: string) => {
        try {
            return moment(cell).format(format);
        } catch (error) {
            return cell;
        }
    };

    const getCellValue = (value: string, item: any) => {
        try {
            const arrayPlus = value.includes('++') ? value.split('++') : value.split('+');
            let response = '';

            for (const string of arrayPlus) {
                let cell2 = item;
                const arrayDocs = string.split('.');
                for (const val of arrayDocs) {
                    cell2 = cell2?.[val];
                    if (cell2 === undefined || cell2 === null) {
                        cell2 = '';
                        break;
                    }
                }
                response += cell2 + (value.includes('++') ? ' ' : '');
            }

            return response.trim();
        } catch (error) {
            return item[value];
        }
    };

    const getColor = (item: any, key: string) => {
        let color = 'purple';
        try {
            let pos_badges = -1;
            if (badges_values && badges_values.length > 0)
                pos_badges = badges_values.findIndex((badge) => badge.col === key);
            if (pos_badges !== -1 && Array.isArray(badges_values)) {
                const current_values = badges_values[pos_badges].values;
                const current = current_values.find((badge) => badge.value == getCellValue(key, item));
                color = current?.color || color;
            }
            return color;
        } catch (error) {
            return color;
        }
    };

    const getCellName = (item: any, key: string) => {
        let response = getCellValue(key, item);
        try {
            let pos_badges = -1;
            if (badges_values && badges_values.length > 0)
                pos_badges = badges_values.findIndex((badge) => badge.col === key);
            if (pos_badges !== -1 && Array.isArray(badges_values)) {
                const current_values = badges_values[pos_badges].values;
                const current = current_values.find((badge) => badge.value == getCellValue(key, item));
                if (current?.name) response = current?.name
            }
            return response;
        } catch (error) {
            return response;
        }
    };

    return (
        <Fragment>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        {headers.map((theader, index) => (
                            <th key={index}>{theader}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <SkeletonTable tr={loadingItems || 5} td={headers.length} />
                    ) : (
                        body?.map((item, index_row) => (
                            <tr key={index_row}>
                                {Array.from({ length: headers.length - 1 }).map((_, index_col) => (
                                    setBadge(index_col, item, index_row)
                                ))}
                                <td>
                                    <Space style={{ justifyContent: 'flex-end', display: 'flex' }}>
                                        {actions ? renderSwitch(item) : null}
                                    </Space>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            {!loading && (body?.length === 0 || !body) && <NoData />}
        </Fragment>
    );
};

export default TableComp;